// Item 3 (Batch 6): standalone public gallery viewer. No PMS auth required.
// Consumers of the shareable link (guests, prospects) land here.
import { useEffect, useState } from "react";
import axios from "axios";
import { Loader2, X } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

export default function PublicGallery() {
  const pid = window.location.pathname.split("/").pop();
  const [data, setData] = useState(null);
  const [err, setErr] = useState("");
  const [lightbox, setLightbox] = useState(null);

  useEffect(() => {
    axios.get(`${API}/api/pms/public/properties/${pid}/gallery`)
      .then((r) => setData(r.data))
      .catch((e) => setErr(e?.response?.data?.detail || "Gallery not found"));
  }, [pid]);

  if (err) return <div className="min-h-screen flex items-center justify-center bg-slate-900 text-white/70 text-sm">{err}</div>;
  if (!data) return <div className="min-h-screen flex items-center justify-center bg-slate-900"><Loader2 className="animate-spin text-white/40" size={28} /></div>;

  return (
    <div className="min-h-screen bg-slate-950 text-white">
      <header className="px-6 md:px-10 py-8 border-b border-white/10">
        <p className="text-[10px] uppercase tracking-[0.25em] text-white/40 font-semibold">Photo Gallery</p>
        <h1 className="font-display font-black text-3xl md:text-4xl mt-1" data-testid="public-gallery-title">{data.property_name}</h1>
        {data.city && <p className="text-white/50 mt-1 text-sm">{data.city}</p>}
      </header>

      <main className="px-6 md:px-10 py-8">
        {data.photos.length === 0 ? (
          <p className="text-center text-white/40 py-24 text-sm">This property hasn't uploaded any photos yet.</p>
        ) : (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
            {data.photos.map((url, i) => (
              <button key={url} onClick={() => setLightbox(i)} className="aspect-[4/3] rounded-lg overflow-hidden bg-white/5 group">
                <img src={url} alt="" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
              </button>
            ))}
          </div>
        )}
      </main>

      <footer className="px-6 md:px-10 py-6 text-center text-[10px] text-white/30 border-t border-white/10">
        Powered by Atiti Homestays Private Limited · <span className="font-mono">{data.photos.length} photo(s)</span>
      </footer>

      {lightbox !== null && (
        <div className="fixed inset-0 bg-black/95 z-50 flex items-center justify-center p-6" onClick={() => setLightbox(null)}>
          <button className="absolute top-4 right-4 text-white/70 hover:text-white" onClick={() => setLightbox(null)}><X size={26} /></button>
          <button className="absolute left-4 top-1/2 -translate-y-1/2 text-white/70 hover:text-white text-4xl"
            onClick={(e) => { e.stopPropagation(); setLightbox((lightbox - 1 + data.photos.length) % data.photos.length); }}>‹</button>
          <img src={data.photos[lightbox]} alt="" className="max-h-[88vh] max-w-[88vw] object-contain" onClick={(e) => e.stopPropagation()} />
          <button className="absolute right-4 top-1/2 -translate-y-1/2 text-white/70 hover:text-white text-4xl"
            onClick={(e) => { e.stopPropagation(); setLightbox((lightbox + 1) % data.photos.length); }}>›</button>
          <p className="absolute bottom-4 left-0 right-0 text-center text-white/60 text-xs">{data.property_name} · {lightbox + 1} / {data.photos.length}</p>
        </div>
      )}
    </div>
  );
}
